import cn from 'classnames'
import { FC, useEffect, useMemo, useRef, useState } from 'react'

import { RenderWhen } from 'clickNgo/components/renderWhen'
import { NumberInput } from 'cng-common/components/numberInput'
import { INumberInputProps } from 'cng-common/components/numberInput/models'
import { getFormattedFinance } from 'cng-common/helpers/numbers'

import styles from './FloatingNumberInput.module.scss'

interface IFloatingNumberInputProps extends Omit<INumberInputProps, 'ref'> {
  floatLength?: number
}

export const FloatingNumberInput: FC<IFloatingNumberInputProps> = ({
  floatLength = 10,
  onBlur,
  onFocus,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const ref = useRef<HTMLInputElement>(null)

  const displayValue = useMemo(() => {
    const rawValue = props.value
    if (!rawValue) {
      return ''
    }

    return getFormattedFinance(rawValue, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  }, [props.value])

  const width = useMemo(() => {
    if (displayValue.length <= floatLength) {
      return
    }

    const step = displayValue.length / 1.6
    return step + 'em'
  }, [displayValue, floatLength])

  useEffect(() => {
    const input = ref.current
    if (isFocused && input) {
      input.focus()
    }
  }, [isFocused])

  return (
    <div className={styles.floatingNumberInputContainer}>
      <NumberInput
        {...props}
        classes={{
          ...props.classes,
          inputClasses: {
            ...props.classes?.inputClasses,
            root: cn(props.classes?.inputClasses?.root, 'floating-number-input'),
          },
        }}
        onFocus={() => {
          setIsFocused(true)
        }}
        value={props.value}
        align={props.align}
        disabled={props.disabled}
        maximumFractionDigits={props.maximumFractionDigits}
        checkFn={props.checkFn}
      />

      <RenderWhen condition={isFocused}>
        <div className={styles.floatingContainer} style={{ width }}>
          <NumberInput
            ref={ref as any}
            classes={{
              ...props.classes,
              inputClasses: {
                ...props.classes?.inputClasses,
                root: cn(props.classes?.inputClasses?.root, 'floating-number-input'),
              },
            }}
            onFocus={() => {
              if (onFocus) {
                onFocus()
              }
            }}
            onBlur={(e) => {
              setIsFocused(false)
              if (onBlur) {
                onBlur(e)
              }
            }}
            {...props}
          />
        </div>
      </RenderWhen>
    </div>
  )
}
